import { useMemo } from 'react';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import { RuleResponse, RuleRequest } from 'store/api/apiTypes';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

export type RuleEndpointName =
  | 'getConversationalWritingRule'
  | 'getStatisticalEvidenceRule'
  | 'getContentFreshnessRule';

export interface UseCachedRuleResult {
  ruleData: RuleResponse | undefined;
  company: RuleResponse['company'] | undefined;
  products: Record<string, any>;
  status: string;
  isLoading?: boolean;
  error?: any;
  queryArgs: RuleRequest | null;
  hasRuleData: boolean;
  isLoaded: boolean;
}

/**
 * useCachedRuleFactory
 * Generic cached selector hook for visibility rules sharing RuleRequest/RuleResponse.
 * Avoids repetition across conversational writing, statistical evidence, content freshness, etc.
 */
export const useCachedRuleFactory = (
  endpointName: RuleEndpointName
): UseCachedRuleResult => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<RuleRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectRule = useMemo(
    () =>
      queryArgs
        ? (rulesApiSlice.endpoints as any)[endpointName].select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs, endpointName]
  );

  const {
    data,
    status,
    isLoading: loading,
    error,
  } = useAppSelector(selectRule as any) as {
    data: RuleResponse | undefined;
    status: string;
    isLoading?: boolean;
    error?: any;
  };

  const isLoaded = !loading && !!data?.products && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    ruleData: data,
    company: data?.company,
    products: data?.products || {},
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    hasRuleData: !!data,
  };
};
